/* istanbul ignore file */
import React, { useEffect, useState } from 'react';
import {
  Access,
  ApplicationArea,
  ObjectType,
  PrincipalType,
  WorklistAccessControl,
  WorklistAccessControlPrincipal,
  WorklistAccessControlToDelete,
  WorklistAccessControlToUpdate,
} from '../../../../api-service/clients/auto-generated-clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { IAutocompleteValue } from '../../save-dialog';
import { ISavedEntityDefinition } from '../../store/reducers/saved-entity-drawer-reducer';
import { SavedEntityFormComponent } from './saved-entity-form-component';

interface ISavedEntityDrawerFormContainerFormProps {
  objectType: ObjectType;
  hasApplicationAreas: boolean;
  savedEntityDefinitions?: ISavedEntityDefinition[];
  area: string;
}
interface ISavedEntityDrawerState {
  savedEntityDefinitions: ISavedEntityDefinition[];
  applicationAreas: ApplicationArea[];
}

export interface WorklistAccessControlPrincipalEx extends WorklistAccessControlPrincipal {
  isNew?: boolean;
  isDeleted?: boolean;
  isUpdated?: boolean;
}

const searchClient = moduleClientFactory.createSearchClient();
const applicationAreaClient = moduleClientFactory.createApplicationAreaClient();

export function SavedEntityFormContainer(props: ISavedEntityDrawerFormContainerFormProps) {
  const { objectType, hasApplicationAreas, area } = props;
  const [worklistAccessControls, setWorklistAccessControls] = useState<WorklistAccessControlPrincipalEx[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [savedEntityDrawerState, setSavedEntityDrawerState] = useState<ISavedEntityDrawerState>({
    savedEntityDefinitions: [],
    applicationAreas: [],
  });

  const { savedEntityDefinitions, applicationAreas } = savedEntityDrawerState;

  useEffect(() => {
    async function loadData() {
      const savedEntityDefinitionCollections = props.savedEntityDefinitions;
      if (savedEntityDefinitionCollections && savedEntityDefinitionCollections.length === 1) {
        const savedEntityDefinition = savedEntityDefinitionCollections[0];
        const { applicationAreaIds = [] } = savedEntityDefinition;

        const applicationAreas = await loadApplicationAreas(applicationAreaIds);
        const worklistItems = await loadWorklistItems(savedEntityDefinition.id);

        setSavedEntityDrawerState({
          ...savedEntityDrawerState,
          savedEntityDefinitions: [savedEntityDefinition],
          applicationAreas,
        });
        setWorklistAccessControls((worklistItems.worklistAccessControls || []) as WorklistAccessControlPrincipalEx[]);
        setIsDrawerOpen(true);
      } else if (savedEntityDefinitionCollections && savedEntityDefinitionCollections.length > 0) {
        setSavedEntityDrawerState({
          ...savedEntityDrawerState,
          savedEntityDefinitions: savedEntityDefinitionCollections,
        });
        setWorklistAccessControls([]);
        setIsDrawerOpen(true);
      }
    }

    loadData();
  }, [props.savedEntityDefinitions]);
  async function loadApplicationAreas(applicationAreaIds: string[]) {
    if (applicationAreaIds.length > 0) {
      const applicationAreas = await applicationAreaClient.getApplicationAreasByIds(applicationAreaIds);
      return applicationAreas;
    }
    return [];
  }
  async function loadWorklistItems(savedEntityId: string) {
    return await searchClient.worklistAccessControlQuery({
      savedEntityId: savedEntityId,
    });
  }
  function handleClose() {
    setIsDrawerOpen(false);

    setSavedEntityDrawerState({ ...savedEntityDrawerState });
  }
  async function handleAddButtonClick(autocompleteValue: IAutocompleteValue, type: PrincipalType) {
    setWorklistAccessControls([
      ...worklistAccessControls,
      {
        pricipalName: autocompleteValue.object as string,
        accessControl: { principalId: autocompleteValue.value, principalType: type, access: Access.Read, objectId: '', objectType },
        isNew: true,
      },
    ]);
  }
  async function handleOkButtonClick() {
    try {
      setIsLoading(true);
      const collectionToCreate: WorklistAccessControl[] = [];
      const collectionToDelete: WorklistAccessControlToDelete[] = [];
      const collectionToUpdate: WorklistAccessControlToUpdate[] = [];
      savedEntityDefinitions.forEach(async x => {
        worklistAccessControls
          .filter(y => y.isNew && !y.isDeleted)
          .forEach(async y => {
            collectionToCreate.push({ ...y.accessControl, objectId: x.id } as WorklistAccessControl);
          });
      });
      savedEntityDefinitions.forEach(async x => {
        worklistAccessControls
          .filter(y => y.isDeleted && !y.isNew)
          .forEach(async y => {
            collectionToDelete.push({ ...y.accessControl, objectId: x.id } as WorklistAccessControlToDelete);
          });
      });
      savedEntityDefinitions.forEach(async x => {
        worklistAccessControls
          .filter(y => y.isUpdated && !y.isDeleted)
          .forEach(async y => {
            collectionToUpdate.push({ ...y.accessControl, objectId: x.id } as WorklistAccessControlToUpdate);
          });
      });
      if (collectionToCreate.length > 0) {
        await searchClient.createWorklistAccessControl({ worklistAccessControls: collectionToCreate });
      }
      if (collectionToUpdate.length > 0) {
        await searchClient.updateWorklistAccessControl({ worklistAccessControlsToUpdate: collectionToUpdate });
      }
      if (collectionToDelete.length > 0) {
        await searchClient.deleteWorklistAccessControl({ worklistAccessControlsToDelete: collectionToDelete });
      }
      setIsDrawerOpen(false);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDeleteClick(principalId: string) {
    setWorklistAccessControls([...worklistAccessControls.map(x => (x.accessControl?.principalId === principalId ? { ...x, isDeleted: true } : x))]);
  }

  async function handleUpdateAccessClick(principalId: string, access: Access) {
    setWorklistAccessControls([
      ...worklistAccessControls.map(x => {
        if (x.accessControl?.principalId === principalId) {
          return { ...x, accessControl: { ...x.accessControl, access }, isUpdated: true };
        }
        return x;
      }),
    ]);
  }

  return (
    <SavedEntityFormComponent
      worklistAccessControls={worklistAccessControls}
      applicationAreas={applicationAreas}
      onClose={handleClose}
      onAddButtonClick={handleAddButtonClick}
      onDeleteClick={handleDeleteClick}
      onUpdateAccessClick={handleUpdateAccessClick}
      isDrawerOpen={isDrawerOpen}
      savedEntityDefinitions={savedEntityDefinitions}
      hasApplicationAreas={hasApplicationAreas}
      onOkButtonClick={handleOkButtonClick}
      area={area}
    />
  );
}
